import { useState, useEffect, useCallback } from "react";
import { Search, Filter, Package, RefreshCw, X, Box, Boxes, AlertCircle, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import axios from "axios";
import MaterialCard from "@/components/MaterialCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [materialType, setMaterialType] = useState("all");
  const [plant, setPlant] = useState("all");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeConnection, setActiveConnection] = useState(null);
  const [dataSource, setDataSource] = useState("loading");
  
  // Fetch active connection
  useEffect(() => {
    const fetchConnection = async () => {
      try {
        const response = await axios.get(`${API}/sap-connections`);
        const connections = response.data.filter(c => c.is_active);
        if (connections.length > 0) {
          setActiveConnection(connections[0]);
        }
      } catch (error) {
        console.error("Failed to fetch connections:", error);
      }
    };
    fetchConnection();
  }, []);
  
  const fetchMaterials = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (materialType !== "all") params.append("material_type", materialType);
      if (plant !== "all") params.append("plant", plant);
      if (activeConnection) params.append("connection_id", activeConnection.id);
      
      const response = await axios.get(`${API}/materials?${params.toString()}`);
      setMaterials(response.data);
      
      setDataSource("sap_live");
    } catch (error) {
      console.error("Failed to fetch materials:", error);
      setMaterials([]);
      const detail = error.response?.data?.detail;
      setDataSource(detail?.code || "SAP_RESPONSE_ERROR");
      toast.error(detail?.message || "Failed to load materials");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery, materialType, plant, activeConnection]);
  
  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);
  
  // Re-fetch when connection changes
  useEffect(() => {
    if (activeConnection) {
      setLoading(true);
      fetchMaterials();
    }
  }, [activeConnection]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchMaterials();
    toast.success("Materials refreshed");
  };
  
  const handleClearFilters = () => {
    setSearchQuery("");
    setMaterialType("all");
    setPlant("all");
  };
  
  const hasActiveFilters = searchQuery || materialType !== "all" || plant !== "all";
  
  // Calculate stats
  const stats = {
    total: new Set(materials.map((m) => m.material_number)).size,
    stockRecords: materials.filter((m) => m.plant).length,
    lowStock: materials.some((m) => Number.isFinite(m.available_quantity))
      ? materials.filter((m) => Number.isFinite(m.available_quantity) && m.stock_quantity > 0 && (m.available_quantity / m.stock_quantity) < 0.3).length
      : null
  };
  
  // Filter out empty plant values
  const uniquePlants = [...new Set(materials.map(m => m.plant).filter(p => p && p.trim() !== ''))];
  
  return (
    <div data-testid="dashboard-page">
      {/* Page Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 mb-6">
        <div>
          <h1 className="page-title heading-display text-2xl font-bold tracking-tight text-slate-900" data-testid="page-title">MATERIAL INVENTORY</h1>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-2 font-medium">
            {dataSource === "sap_live" ? (
              <><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Live data from SAP S/4HANA</>
            ) : dataSource === "loading" ? (
              <><span className="w-2 h-2 rounded-full bg-blue-500" />Loading SAP data...</>
            ) : (
              <><span className="w-2 h-2 rounded-full bg-red-500" />{dataSource === "NO_SAP_CONNECTION" ? "No SAP connection" : dataSource === "SAP_UNREACHABLE" ? "SAP unreachable" : "SAP data unavailable"}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 font-medium hidden md:inline">Last updated: 17 Sep 2026, 04:07 AM</span>
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="h-9 px-4 rounded-xl border-slate-200/80 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold shadow-xs"
            disabled={refreshing}
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 text-slate-500 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Card 1: Total Materials */}
        <div className="stat-card p-4 rounded-2xl border border-blue-100 bg-blue-50/50 shadow-xs hover:shadow-md transition-all cursor-pointer flex items-center justify-between" data-testid="stat-total-materials">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm">
              <Box className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight text-slate-900 leading-none">{stats.total}</p>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-1.5">TOTAL MATERIALS</p>
            </div>
          </div>
          <div className="w-8 h-8 rounded-full bg-blue-100/70 text-blue-700 flex items-center justify-center">
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>

        {/* Card 2: Plant Stock Records */}
        <div className="stat-card p-4 rounded-2xl border border-emerald-100 bg-emerald-50/50 shadow-xs hover:shadow-md transition-all cursor-pointer flex items-center justify-between" data-testid="stat-total-stock">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight text-slate-900 leading-none">{stats.stockRecords.toLocaleString()}</p>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-1.5">PLANT STOCK RECORDS</p>
            </div>
          </div>
          <div className="w-8 h-8 rounded-full bg-emerald-100/70 text-emerald-700 flex items-center justify-center">
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>

        {/* Card 3: Low Stock Alerts */}
        <div className="stat-card p-4 rounded-2xl border border-amber-100 bg-amber-50/50 shadow-xs hover:shadow-md transition-all cursor-pointer flex items-center justify-between" data-testid="stat-low-stock">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-sm">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight text-slate-900 leading-none">{stats.lowStock ?? "-"}</p>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-1.5">LOW STOCK (WHEN AVAILABLE)</p>
            </div>
          </div>
          <div className="w-8 h-8 rounded-full bg-amber-100/70 text-amber-700 flex items-center justify-center">
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </div>
      
      {/* Filter Bar */}
      <div className="filter-bar" data-testid="filter-bar">
        <div className="search-input-wrapper flex-1 min-w-[200px] max-w-md">
          <Search />
          <Input
            placeholder="Search materials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <Select value={materialType} onValueChange={setMaterialType}>
          <SelectTrigger className="w-[150px]" data-testid="filter-type">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="RAW">Raw Material</SelectItem>
            <SelectItem value="SPARE">Spare Part</SelectItem>
            <SelectItem value="CONS">Consumable</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={plant} onValueChange={setPlant}>
          <SelectTrigger className="w-[140px]" data-testid="filter-plant">
            <SelectValue placeholder="Plant" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plants</SelectItem>
            {uniquePlants.map(p => (
              <SelectItem key={p} value={p}>Plant {p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {hasActiveFilters && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClearFilters}
            data-testid="clear-filters"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
        
        {hasActiveFilters && (
          <Badge variant="secondary" className="ml-auto">
            {materials.length} results
          </Badge>
        )}
      </div>
      
      {/* Materials Grid */}
      {loading ? (
        <div className="bento-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-[320px] rounded-md" />
          ))}
        </div>
      ) : materials.length === 0 ? (
        <div className="empty-state" data-testid="empty-state">
          <Package className="empty-state-icon" />
          <h3 className="text-lg font-medium mb-2">No Materials Found</h3>
          <p className="text-muted-foreground mb-4">
            {hasActiveFilters 
              ? "Try adjusting your filters or search query"
              : "Connect to a SAP system to view materials"
            }
          </p>
          {hasActiveFilters && (
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="bento-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="materials-grid">
          {materials.map((material) => (
            <MaterialCard
              key={`${material.material_number}-${material.plant || "all"}`}
              material={material}
              onClick={setSelectedMaterial}
            />
          ))}
        </div>
      )}
      
      {/* Material Detail Dialog */}
      <Dialog open={!!selectedMaterial} onOpenChange={() => setSelectedMaterial(null)}>
        <DialogContent className="max-w-lg" data-testid="material-detail-dialog" aria-describedby="material-detail-description">
          {selectedMaterial && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                    <Package className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <span className="data-value block">{selectedMaterial.material_number}</span>
                    <DialogDescription id="material-detail-description" className="text-sm text-muted-foreground font-normal">
                      {selectedMaterial.description}
                    </DialogDescription>
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Type</p>
                    <Badge variant="secondary">{selectedMaterial.material_type}</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Group</p>
                    <p className="data-value">{selectedMaterial.material_group}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Base Unit</p>
                    <p className="data-value">{selectedMaterial.base_unit}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Plant</p>
                    <p className="data-value">{selectedMaterial.plant}</p>
                  </div>
                </div>
                
                {/* Stock Details */}
                <div className="p-4 bg-secondary/50 rounded-md">
                  <h4 className="font-medium mb-4">Stock Information</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="data-value text-2xl">{selectedMaterial.stock_quantity.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Total Stock</p>
                    </div>
                    <div>
                      <p className="data-value text-2xl text-emerald-600">
                        {Number.isFinite(selectedMaterial.available_quantity) ? selectedMaterial.available_quantity.toLocaleString() : "-"}
                      </p>
                      <p className="text-xs text-muted-foreground">Available</p>
                    </div>
                    <div>
                      <p className="data-value text-2xl text-amber-600">
                        {Number.isFinite(selectedMaterial.reserved_quantity) ? selectedMaterial.reserved_quantity.toLocaleString() : "-"}
                      </p>
                      <p className="text-xs text-muted-foreground">Reserved</p>
                    </div>
                  </div>
                </div>
                
                {/* Last Updated */}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Last Updated</span>
                  <span className="data-value">
                    {selectedMaterial.last_updated ? new Date(selectedMaterial.last_updated).toLocaleString() : "-"}
                  </span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
